/**
 * Ticket Detail Page
 * Full ticket view with risk breakdown, SLA tracking, comments, and audit trail.
 * Phase 4: Comments are now fetched and displayed inline.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ticketApi } from '../api/client';
import {
  ArrowLeft, Clock, Shield, MessageSquare, Activity,
  AlertTriangle, User, Server, Send, Lock,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

const priorityColors = {
  critical: 'text-red-400 bg-red-500/15',
  high: 'text-amber-400 bg-amber-500/15',
  medium: 'text-blue-400 bg-blue-500/15',
  low: 'text-gray-400 bg-gray-500/15',
};

const statusColors = {
  new: 'text-blue-400', open: 'text-emerald-400', in_progress: 'text-amber-400',
  escalated: 'text-red-400', on_hold: 'text-gray-400', pending_client: 'text-purple-400',
  resolved: 'text-emerald-500', closed: 'text-gray-500',
};

export default function TicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isSentinelUser, user } = useAuth();
  const basePath = isSentinelUser ? '/sentinel' : '/portal';

  const [ticket, setTicket] = useState(null);
  const [activities, setActivities] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function loadAll() {
    try {
      const [ticketRes, activityRes, commentsRes] = await Promise.all([
        ticketApi.get(id),
        ticketApi.getActivity(id),
        ticketApi.getComments(id),
      ]);
      setTicket(ticketRes.data);
      setActivities(activityRes.data);
      setComments(commentsRes.data);
    } catch (err) {
      console.error('Failed to load ticket:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, [id]);

  async function handleComment(e) {
    e.preventDefault();
    if (!comment.trim()) return;
    setSubmitting(true);
    try {
      await ticketApi.addComment(id, { content: comment, is_internal: isInternal });
      setComment('');
      setIsInternal(false);
      await loadAll();
    } catch (err) {
      console.error('Failed to add comment:', err);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleStatusChange(newStatus) {
    try {
      const res = await ticketApi.update(id, { status: newStatus });
      setTicket(res.data);
      const actRes = await ticketApi.getActivity(id);
      setActivities(actRes.data);
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-800 rounded w-64" />
          <div className="h-48 bg-gray-800 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!ticket) {
    return <div className="p-8 text-center text-gray-500">Ticket not found</div>;
  }

  const riskColor = ticket.risk_score >= 75 ? 'text-red-400' :
                    ticket.risk_score >= 50 ? 'text-amber-400' :
                    ticket.risk_score >= 25 ? 'text-blue-400' : 'text-emerald-400';

  return (
    <div className="p-8 max-w-6xl">
      {/* Back + Header */}
      <button
        onClick={() => navigate(`${basePath}/tickets`)}
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-300 mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to tickets
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <span className="text-sm text-gray-500 font-mono">{ticket.ticket_number}</span>
            <span className={`text-sm font-medium ${statusColors[ticket.status]}`}>
              {ticket.status.replace('_', ' ')}
            </span>
          </div>
          <h1 className="text-xl font-semibold text-white">{ticket.title}</h1>
        </div>

        {isSentinelUser && !['resolved', 'closed'].includes(ticket.status) && (
          <div className="flex items-center gap-2">
            {ticket.status === 'new' && (
              <button onClick={() => handleStatusChange('in_progress')}
                className="px-3 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors">
                Start working
              </button>
            )}
            <button onClick={() => handleStatusChange('resolved')}
              className="px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg transition-colors">
              Resolve
            </button>
            <button onClick={() => handleStatusChange('escalated')}
              className="px-3 py-2 text-sm bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg transition-colors">
              Escalate
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main column */}
        <div className="col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <h3 className="text-sm font-medium text-gray-400 mb-3">Description</h3>
            <p className="text-sm text-gray-300 whitespace-pre-wrap">
              {ticket.description || 'No description provided.'}
            </p>
          </div>

          {/* Risk breakdown */}
          {ticket.risk_factors && Object.keys(ticket.risk_factors).length > 0 && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-4 h-4 text-gray-400" />
                <h3 className="text-sm font-medium text-gray-400">Risk breakdown</h3>
                <span className={`ml-auto text-2xl font-bold ${riskColor}`}>{ticket.risk_score}</span>
              </div>
              <div className="space-y-2">
                {Object.entries(ticket.risk_factors).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">{key.replace(/_/g, ' ')}</span>
                    <span className="text-sm font-medium text-gray-300">+{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comments section */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-4 h-4 text-gray-400" />
              <h3 className="text-sm font-medium text-gray-400">Comments</h3>
              <span className="text-xs text-gray-600">{comments.length}</span>
            </div>

            {/* Existing comments */}
            {comments.length > 0 && (
              <div className="space-y-3 mb-6">
                {comments.map((c) => (
                  <div key={c.id} className={`rounded-lg p-4 ${c.is_internal ? 'bg-amber-500/5 border border-amber-500/15' : 'bg-gray-800/60 border border-gray-700/50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-[10px] font-medium text-gray-300">
                          {c.user?.first_name?.[0]}{c.user?.last_name?.[0]}
                        </div>
                        <span className="text-sm font-medium text-gray-200">
                          {c.user ? `${c.user.first_name} ${c.user.last_name}` : 'Unknown'}
                        </span>
                        {c.is_internal && (
                          <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">
                            <Lock className="w-2.5 h-2.5" /> Internal
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-600">
                        {c.created_at ? formatDistanceToNow(new Date(c.created_at), { addSuffix: true }) : ''}
                      </span>
                    </div>
                    <p className="text-sm text-gray-300 whitespace-pre-wrap">{c.content}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Comment form */}
            <form onSubmit={handleComment}>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a comment..."
                rows={3}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-600 resize-none"
              />
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-2">
                  {isSentinelUser && (
                    <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isInternal}
                        onChange={(e) => setIsInternal(e.target.checked)}
                        className="rounded border-gray-600"
                      />
                      Internal note (hidden from client)
                    </label>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={submitting || !comment.trim()}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <Send className="w-3.5 h-3.5" />
                  Send
                </button>
              </div>
            </form>
          </div>

          {/* Activity timeline */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-4 h-4 text-gray-400" />
              <h3 className="text-sm font-medium text-gray-400">Activity</h3>
            </div>
            <div className="space-y-3">
              {activities.map((act) => (
                <div key={act.id} className="flex items-start gap-3 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-600 mt-2 flex-shrink-0" />
                  <div className="flex-1">
                    <span className="text-gray-300">
                      {act.user ? `${act.user.first_name} ${act.user.last_name}` : 'System'}
                    </span>
                    <span className="text-gray-500 ml-1.5">
                      {act.activity.replace(/_/g, ' ')}
                    </span>
                    {act.new_value && (
                      <span className="text-gray-400 ml-1">{'\u2192'} {act.new_value}</span>
                    )}
                  </div>
                  <span className="text-xs text-gray-600 flex-shrink-0">
                    {formatDistanceToNow(new Date(act.created_at), { addSuffix: true })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 uppercase tracking-wider">Priority</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${priorityColors[ticket.priority]}`}>
                {ticket.priority}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 uppercase tracking-wider">Risk score</span>
              <span className={`text-lg font-bold ${riskColor}`}>{ticket.risk_score}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 uppercase tracking-wider">Source</span>
              <span className="text-sm text-gray-300">{ticket.source}</span>
            </div>
            {ticket.category && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 uppercase tracking-wider">Category</span>
                <span className="text-sm text-gray-300">{ticket.category}</span>
              </div>
            )}
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-gray-500" />
              <span className="text-xs text-gray-500 uppercase tracking-wider">SLA tracking</span>
            </div>
            {ticket.sla_response_due && (
              <div>
                <div className="text-xs text-gray-500">Response due</div>
                <div className="text-sm text-gray-300">
                  {format(new Date(ticket.sla_response_due), 'MMM d, h:mm a')}
                </div>
                {ticket.sla_response_met === true && <span className="text-xs text-emerald-400">Met</span>}
                {ticket.sla_response_met === false && <span className="text-xs text-red-400">Breached</span>}
              </div>
            )}
            {ticket.sla_resolution_due && (
              <div>
                <div className="text-xs text-gray-500">Resolution due</div>
                <div className="text-sm text-gray-300">
                  {format(new Date(ticket.sla_resolution_due), 'MMM d, h:mm a')}
                </div>
                {ticket.sla_resolution_met === true && <span className="text-xs text-emerald-400">Met</span>}
                {ticket.sla_resolution_met === false && <span className="text-xs text-red-400">Breached</span>}
              </div>
            )}
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <User className="w-4 h-4 text-gray-500" />
              <span className="text-xs text-gray-500 uppercase tracking-wider">People</span>
            </div>
            <div>
              <div className="text-xs text-gray-500">Created by</div>
              <div className="text-sm text-gray-300">{ticket.creator?.first_name} {ticket.creator?.last_name}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Assigned to</div>
              <div className="text-sm text-gray-300">
                {ticket.assignee ? `${ticket.assignee.first_name} ${ticket.assignee.last_name}` : 'Unassigned'}
              </div>
            </div>
          </div>

          {ticket.asset && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2 mb-1">
                <Server className="w-4 h-4 text-gray-500" />
                <span className="text-xs text-gray-500 uppercase tracking-wider">Linked asset</span>
              </div>
              <div className="text-sm text-gray-300">{ticket.asset.name}</div>
              <div className="text-xs text-gray-500">{ticket.asset.asset_type} {'\u00b7'} {ticket.asset.criticality} criticality</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
